import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

type MobileMenuContextType = {
  // Bottom sheet "Plus"
  isMoreSheetOpen: boolean
  openMoreSheet: () => void
  closeMoreSheet: () => void
  toggleMoreSheet: () => void
  // Panel requests (pour communiquer avec Main.jsx et Tools)
  layerPanelRequested: boolean
  snowPanelRequested: boolean
  gisPanelRequested: boolean
  requestLayerPanel: () => void
  requestSnowPanel: () => void
  requestGisPanel: () => void
  clearPanelRequests: () => void
}

const MobileMenuContext = createContext<MobileMenuContextType>({
  isMoreSheetOpen: false,
  openMoreSheet: () => {},
  closeMoreSheet: () => {},
  toggleMoreSheet: () => {},
  layerPanelRequested: false,
  snowPanelRequested: false,
  gisPanelRequested: false,
  requestLayerPanel: () => {},
  requestSnowPanel: () => {},
  requestGisPanel: () => {},
  clearPanelRequests: () => {},
})

export const MobileMenuProvider = ({ children }: { children: ReactNode }) => {
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false)
  const [layerPanelRequested, setLayerPanelRequested] = useState(false)
  const [snowPanelRequested, setSnowPanelRequested] = useState(false)
  const [gisPanelRequested, setGisPanelRequested] = useState(false)

  const openMoreSheet = useCallback(() => setIsMoreSheetOpen(true), [])
  const closeMoreSheet = useCallback(() => setIsMoreSheetOpen(false), [])
  const toggleMoreSheet = useCallback(
    () => setIsMoreSheetOpen(prev => !prev),
    [],
  )

  const requestLayerPanel = useCallback(() => {
    setLayerPanelRequested(true)
    setSnowPanelRequested(false)
    setGisPanelRequested(false)
  }, [])

  const requestSnowPanel = useCallback(() => {
    setSnowPanelRequested(true)
    setLayerPanelRequested(false)
    setGisPanelRequested(false)
  }, [])

  const requestGisPanel = useCallback(() => {
    setGisPanelRequested(true)
    setLayerPanelRequested(false)
    setSnowPanelRequested(false)
  }, [])

  const clearPanelRequests = useCallback(() => {
    setLayerPanelRequested(false)
    setSnowPanelRequested(false)
    setGisPanelRequested(false)
  }, [])

  return (
    <MobileMenuContext.Provider
      value={{
        isMoreSheetOpen,
        openMoreSheet,
        closeMoreSheet,
        toggleMoreSheet,
        layerPanelRequested,
        snowPanelRequested,
        gisPanelRequested,
        requestLayerPanel,
        requestSnowPanel,
        requestGisPanel,
        clearPanelRequests,
      }}
    >
      {children}
    </MobileMenuContext.Provider>
  )
}

export const useMobileMenu = () => useContext(MobileMenuContext)
